import { FC, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { cloneDeep, isEmpty, isNil, noop } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Types
import type { AwardById, Campaign, ExclusionRulesListRowData, TemplateById } from 'types';
//Import Components
import { BusyLoader } from '@geneui/components';
import CampaignApprovalInfoModalBody from './CampaignApprovalInfoModalBody';
import CampaignInfoModalBody from './CampaignInfoModalBody';
import TemplateInfoModalBody from '../TemplateModal/TemplateInfoModalBody';
import CampaignBonusInfoModalBody from './CampaignBonusInfoModalBody';
import CampaignABTestInfoModal from './CampaignABTestInfoModal';
import CustomTabs from 'components/common/CustomTabs';
import { SegmentModal } from 'components/SegmentModal';
// Import Constants
import { l } from 'constants/common';
// Import Services
import {
    CampaignHttpService,
    TemplateHttpService,
    BonusHttpService,
    UtilsHttpService,
    ExclusionRulesHttpService,
} from 'services/http';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Config
import { CampaignInfoModalCustomTabsConfig } from './config';
import { tabsKey } from '../Campaign/config';
//Import Version History
import { useVersionHistoryContext } from 'components/VersionHistory';

interface Props {
    campaignId: string | number;
    approvalRequestId?: Nilable<number>;
    handleInfoModalActionClick?: (...args: AnyObject[]) => void;
    templateProviders?: AnyObject;
    isInReportPage?: boolean;
    withActionButtons?: boolean;
    className?: string;
}

const CampaignInfoContent: FC<Props> = ({
    campaignId,
    approvalRequestId,
    handleInfoModalActionClick = noop,
    templateProviders,
    isInReportPage,
    withActionButtons = true,
    className,
}) => {
    const { t } = useTranslation();
    const { doGetRequest, doPostRequest } = useRequest();

    const { getTemplate, getCampaign, getBonusById, getScheduleRunDatesRequest, getExclusionRulesListRequest } =
        useCancelableRequests(
            {
                getCampaign: CampaignHttpService.getCampaignById,
                getTemplate: TemplateHttpService.getTemplate,
                getBonusById: BonusHttpService.getBonusById,
                getScheduleRunDatesRequest: UtilsHttpService.getScheduleRunDates,
                getExclusionRulesListRequest: ExclusionRulesHttpService.getExclusionRulesList,
            },
            { takeLatest: ['getCampaign', 'getBonusById'] },
        );

    const [campaignInfo, setCampaignInfo] = useState<Nullable<Campaign>>(null);
    const [templateInfo, setTemplateInfo] = useState<Nullable<TemplateById>>(null);
    const [bonusInfo, setBonusInfo] = useState<Nullable<AwardById>>(null);
    const [tabActiveKey, setTabActiveKey] = useState(
        isNil(approvalRequestId) ? tabsKey.Campaign : tabsKey.PromoDetails,
    );
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
    const [isLoadingBonus, setIsLoadingBonus] = useState(false);
    const [recurrenceDataLocal, setRecurrenceDataLocal] = useState({});
    const [exclusionRulesData, setExclusionRulesData] = useState<ExclusionRulesListRowData[]>([]);

    const isRequestedTabsData = useRef({ [tabsKey.Template]: false, [tabsKey.Bonus]: false });

    const { pointInTime, isHistoryMode } = useVersionHistoryContext();
    const [isInitiated, setIsInitiated] = useState(false);

    const isVisibleActionButtons = withActionButtons && !isHistoryMode;

    const getTemplateData = (templateId, pointInTime): Promise<TemplateById> => {
        return new Promise((resolve, reject) => {
            doGetRequest(getTemplate.request, {
                queryString: { templateId: templateId, pointInTime },
                successCallback: (Data) => {
                    resolve(Data);
                },
                errorCallback: (error) => {
                    reject(error);
                },
            });
        });
    };

    const getTemplateInfo = () => {
        if (!isRequestedTabsData.current[tabsKey.Template]) {
            isRequestedTabsData.current[tabsKey.Template] = true;
            setIsLoadingTemplate(true);
            getTemplateData(campaignInfo?.TemplateId, pointInTime)
                .then((data) => {
                    setTemplateInfo(data);
                })
                .finally(() => {
                    setIsLoadingTemplate(false);
                });
        }
    };

    const getBonusInfo = () => {
        const bonusId = campaignInfo?.Bonus?.BonusId;
        if (!isRequestedTabsData.current[tabsKey.Bonus] && !isNil(bonusId)) {
            isRequestedTabsData.current[tabsKey.Bonus] = true;
            setIsLoadingBonus(true);
            doGetRequest(getBonusById.request, {
                queryString: { bonusId, pointInTime },
                successCallback: (Data) => {
                    setBonusInfo(Data);
                },
            }).then(({ IsCanceled }) => {
                // A superseded call must not hide the loader of the call that replaced it
                if (!IsCanceled) {
                    setIsLoadingBonus(false);
                }
            });
        }
    };

    const getContent = (tabKey) => {
        // eslint-disable-next-line default-case
        switch (tabKey) {
            case tabsKey.PromoDetails:
                return <CampaignApprovalInfoModalBody approvalRequest={campaignInfo?.ApprovalRequest} />;
            case tabsKey.Campaign:
                return (
                    <CampaignInfoModalBody
                        recurrenceData={recurrenceDataLocal}
                        campaignInfo={campaignInfo}
                        handleInfoModalActionClick={handleInfoModalActionClick}
                        isInReportPage={isInReportPage}
                        withActionButtons={isVisibleActionButtons}
                        exclusionRulesData={exclusionRulesData}
                    />
                );
            case tabsKey.Segment:
                return (
                    <SegmentModal
                        segmentId={campaignInfo?.SegmentId}
                        fromCampaignModal
                        withActionButtons={withActionButtons}
                        handleInfoModalActionClick={handleInfoModalActionClick}
                    />
                );
            case tabsKey.Template:
                return (
                    <TemplateInfoModalBody
                        getTemplateInfo={getTemplateData}
                        isLoading={isLoadingTemplate}
                        childItemsInfo={templateInfo?.ChildItemsInfo}
                        templateId={templateInfo?.TemplateId}
                        templateInfo={templateInfo}
                        withActionButtons={isVisibleActionButtons}
                        handleInfoModalActionClick={handleInfoModalActionClick}
                        templateProviders={templateProviders}
                    />
                );
            case tabsKey.Bonus:
                return (
                    <CampaignBonusInfoModalBody
                        campaignBonus={campaignInfo?.Bonus}
                        bonusInfo={bonusInfo}
                        isLoading={isLoadingBonus}
                    />
                );
            case tabsKey.ABTest:
                return (
                    <CampaignABTestInfoModal
                        campaignInfo={campaignInfo}
                        handleInfoModalActionClick={handleInfoModalActionClick}
                    />
                );
        }
    };

    const tabChangeHandler = (tabKey) => {
        return () => {
            if (tabKey === tabsKey.Template && isNil(templateInfo)) {
                getTemplateInfo();
            } else if (tabKey === tabsKey.Bonus && isNil(bonusInfo)) {
                getBonusInfo();
            }
            setTabActiveKey(tabKey);
        };
    };

    useEffect(() => {
        if (!isInitiated) {
            setIsInitiated(true);
            return;
        }

        if (tabActiveKey === tabsKey.Template) {
            setIsLoadingTemplate(true);
            getTemplateData(campaignInfo?.TemplateId, pointInTime)
                .then((data) => {
                    setTemplateInfo(data);
                })
                .finally(() => {
                    setIsLoadingTemplate(false);
                });
        } else if (tabActiveKey === tabsKey.Bonus && !isNil(campaignInfo?.Bonus?.BonusId)) {
            const bonusId = campaignInfo!.Bonus!.BonusId;
            setIsLoadingBonus(true);
            doGetRequest(getBonusById.request, {
                queryString: { bonusId, pointInTime },
                successCallback: (Data) => {
                    setBonusInfo(Data);
                },
            }).then(({ IsCanceled }) => {
                // A superseded call must not hide the loader of the call that replaced it
                if (!IsCanceled) {
                    setIsLoadingBonus(false);
                }
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pointInTime]);

    const tabsGenerator = () => {
        return CampaignInfoModalCustomTabsConfig.filter(({ tabKeyForFilter, tabKey }) => {
            if (tabKey === tabsKey.PromoDetails) {
                return !isNil(approvalRequestId) && !isNil(campaignInfo?.[tabKeyForFilter]);
            } else if (tabKey === tabsKey.Template) {
                return campaignInfo?.[tabKeyForFilter];
            } else if (tabKey === tabsKey.ABTest) {
                return (campaignInfo?.ABTests?.length ?? 0) > 0;
            } else {
                return campaignInfo?.[tabKeyForFilter] || campaignInfo?.Bonus?.BonusId;
            }
        }).map(({ tabKey, buttonContent }) => {
            return {
                tabKey,
                tabTitle: buttonContent(tabChangeHandler, t(l[tabKey])),
                tabContent: getContent(tabKey),
            };
        });
    };

    useEffect(() => {
        if (isNil(campaignInfo)) {
            return;
        }

        const tabs = tabsGenerator();

        const isActiveTabAvailable = tabs.some((tab) => tab.tabKey === tabActiveKey);

        if (!isActiveTabAvailable) {
            setTabActiveKey(tabsKey.Campaign);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignInfo]);

    const init = () => {
        setIsLoading(true);
        doGetRequest(getCampaign.request, {
            queryString: { campaignId, pointInTime, approvalRequestId },
            successCallback: (Data) => {
                const campaignInfoData = cloneDeep(Data);

                if (!isEmpty(Data.ExclusionRules)) {
                    doGetRequest(getExclusionRulesListRequest.request, {
                        successCallback: (data) => {
                            setExclusionRulesData(
                                data.filter((rule) => Data.ExclusionRules.includes(rule.ExclusionRuleId)),
                            );
                        },
                    });
                }

                if (!isNil(Data?.Schedule)) {
                    if (!isNil(Data.Schedule?.CronSchedule) && Data.Schedule?.RecurrenceCount) {
                        if (campaignInfoData.Schedule) {
                            campaignInfoData.Schedule.ScheduleType = l.Repeat as unknown as number;
                        }
                        doPostRequest(getScheduleRunDatesRequest.request, {
                            requestBody: {
                                cronExpression: Data.Schedule.CronSchedule,
                                startDate: Data.Schedule.StartDate,
                                endDate: Data.Schedule.EndDate,
                                timeZone: Data.Schedule.TimeZone,
                            },
                            successCallback: (Data) => {
                                setRecurrenceDataLocal(Data);
                            },
                        });
                    } else {
                        if (campaignInfoData.Schedule) {
                            campaignInfoData.Schedule.ScheduleType = l.OneTime as unknown as number;
                        }
                    }
                }
                setCampaignInfo(campaignInfoData);

                setIsLoading(false);
            },
        });
    };

    const cleanUp = () => {
        return () => {
            setCampaignInfo(null);
            setIsLoading(true);
        };
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, [pointInTime]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(cleanUp, []);

    return (
        <div className={classNames('crm-campaign-info-popup', className)}>
            <BusyLoader isBusy={isLoading} type="spinner" spinnerSize="medium">
                {!isNil(campaignInfo) && (
                    <CustomTabs
                        activeTabKey={tabActiveKey}
                        tabsConfig={tabsGenerator()}
                        tabChangeHandler={tabChangeHandler}
                    />
                )}
            </BusyLoader>
        </div>
    );
};

export default CampaignInfoContent;
